import { useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import { getOrderData, getAllOrderData, updateOrderStatus } from "../services/order-service.js";
import { getTranslation } from "../utils/translate.js";
import { useLanguage } from "../utils/languageContext.js";

const defaultStatusForTab = (tab) => {
    if (tab === "history") return "DELIVERED,DECLINED";
    /** Ongoing (orders + dishes subtabs): identical backend query. */
    return "REQUESTED,PREPARING,READY_FOR_DELIVERY,DELIVERY_IN_PROGRESS";
};

const isUnboundedDeliveryQuery = (query) =>
    !query.deliveryDateFrom && !query.deliveryDateTo && !query.orderNumber;

const buildQueryFrom = ({
    activeTab,
    currentPage,
    itemsPerPage,
    statusKey,
    paymentKey,
    companyKey,
    orderNumberKey,
    deliveryDateFrom,
    deliveryDateTo
}) => {
    const statusQuery = statusKey || defaultStatusForTab(activeTab);
    const paymentDefaults = ["PAYED", "NOT_PAYED"];
    const paymentQuery = paymentKey || paymentDefaults.join(",");
    const sendPaymentStatus = activeTab !== "ongoing";
    const limit = itemsPerPage;
    const offset = Math.max(0, (currentPage - 1) * itemsPerPage);
    const useDateRange = !orderNumberKey && Boolean(deliveryDateFrom && deliveryDateTo);
    const companyId = companyKey
        ? companyKey.split("\u001f").filter((id) => id !== "")
        : [];

    return {
        status: statusQuery,
        paymentStatus: sendPaymentStatus ? paymentQuery : undefined,
        companyId: companyId.length > 0 ? companyId : undefined,
        orderNumber: orderNumberKey || undefined,
        deliveryDateFrom: useDateRange ? deliveryDateFrom : undefined,
        deliveryDateTo: useDateRange ? deliveryDateTo : undefined,
        limit,
        offset,
        sortOrder: activeTab === "history" ? "desc" : "asc"
    };
};

/**
 * Custom hook for managing orders
 * @param {Object} options
 * @param {string} options.chefId
 * @param {string} options.activeTab - 'ongoing' | 'history'
 * @param {number} options.currentPage
 * @param {number} options.itemsPerPage
 * @param {Object} options.toastRef
 * @param {string[]} options.statusFilterValues - Empty = use tab default statuses
 * @param {string[]} options.paymentStatusFilterValues - Empty = PAYED,NOT_PAYED
 * @param {string[]} options.companyFilterValues
 * @param {string} [options.orderNumber] - Substring of order.number; when set, delivery date bounds are omitted
 * @param {string|null} options.deliveryDateFrom - ISO local date-time (history default; ongoing when filter set)
 * @param {string|null} options.deliveryDateTo - ISO local date-time (history default; ongoing when filter set)
 * @returns {Object} Orders state and methods
 */
export const useOrders = ({
    chefId,
    activeTab,
    currentPage,
    itemsPerPage,
    toastRef,
    statusFilterValues = [],
    paymentStatusFilterValues = [],
    companyFilterValues = [],
    orderNumber = "",
    deliveryDateFrom = null,
    deliveryDateTo = null
}) => {
    const { language } = useLanguage();
    const [orderData, setOrderData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [newOrdersCount, setNewOrdersCount] = useState(0);
    const fetchSeqRef = useRef(0);
    /** Ids known to the displayed list (all pages); polls compare against this set. Null = not captured yet. */
    const baselineIdsRef = useRef(null);
    const baselineSeqRef = useRef(0);
    const hasListDataRef = useRef(false);
    const paramsRef = useRef({});
    const inFlightQueryRef = useRef("");

    const statusKey = Array.isArray(statusFilterValues) ? statusFilterValues.join(",") : "";
    const paymentKey = Array.isArray(paymentStatusFilterValues)
        ? paymentStatusFilterValues.join(",")
        : "";
    const companyKey = Array.isArray(companyFilterValues) ? companyFilterValues.join("\u001f") : "";
    const orderNumberKey = orderNumber != null ? String(orderNumber).trim() : "";

    const queryKey = [
        chefId ?? "",
        activeTab,
        String(currentPage),
        String(itemsPerPage),
        statusKey,
        paymentKey,
        companyKey,
        orderNumberKey,
        deliveryDateFrom ?? "",
        deliveryDateTo ?? ""
    ].join("|");

    paramsRef.current = {
        chefId,
        activeTab,
        currentPage,
        itemsPerPage,
        statusKey,
        paymentKey,
        companyKey,
        orderNumberKey,
        deliveryDateFrom,
        deliveryDateTo,
        queryKey
    };

    useLayoutEffect(() => {
        setOrderData([]);
        setTotalCount(0);
        setNewOrdersCount(0);
        baselineIdsRef.current = null;
        hasListDataRef.current = false;
        inFlightQueryRef.current = "";
    }, [activeTab, chefId]);

    const fetchOrders = useCallback(async ({ force = false } = {}) => {
        const p = paramsRef.current;
        if (!p.chefId) return;

        const query = buildQueryFrom(p);
        if (!force && inFlightQueryRef.current === p.queryKey) {
            return;
        }
        inFlightQueryRef.current = p.queryKey;
        const seq = ++fetchSeqRef.current;

        if (hasListDataRef.current) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }
        setError(null);
        setNewOrdersCount(0);
        baselineIdsRef.current = null;

        try {
            const orders = await getOrderData(p.chefId, query);

            if (seq !== fetchSeqRef.current) {
                return;
            }

            setTotalCount(typeof orders?.count === "number" ? orders.count : 0);

            const allOrders = orders?.orderDtoTreeSet || [];
            const validOrders = allOrders.filter((order) => order != null && order.status != null);
            const pageSize = typeof query.limit === "number" && query.limit > 0 ? query.limit : validOrders.length;
            setOrderData(validOrders.slice(0, pageSize));
            hasListDataRef.current = validOrders.length > 0 || (orders?.count ?? 0) > 0;
            setError(null);

            const pageIds = validOrders.filter((o) => o?.id != null).map((o) => o.id);
            const pageHasEverything = query.offset === 0 && allOrders.length >= (orders?.count ?? 0);
            if (pageHasEverything || isUnboundedDeliveryQuery(query)) {
                baselineIdsRef.current = new Set(pageIds);
            } else {
                const baselineSeq = ++baselineSeqRef.current;
                const chefIdAtStart = p.chefId;
                getAllOrderData(chefIdAtStart, query)
                    .then((all) => {
                        if (baselineSeq === baselineSeqRef.current && seq === fetchSeqRef.current) {
                            baselineIdsRef.current = new Set(
                                (all?.orderDtoTreeSet || []).filter((o) => o?.id != null).map((o) => o.id)
                            );
                        }
                    })
                    .catch((err) => console.error("Error capturing order baseline:", err));
            }
        } catch (err) {
            console.error("Error fetching orders:", err);
            if (seq !== fetchSeqRef.current) {
                return;
            }
            setError(err);
            setOrderData([]);
            setTotalCount(0);
            hasListDataRef.current = false;
        } finally {
            if (seq === fetchSeqRef.current) {
                setLoading(false);
                setRefreshing(false);
                if (inFlightQueryRef.current === p.queryKey) {
                    inFlightQueryRef.current = "";
                }
            }
        }
    }, []);

    useEffect(() => {
        if (!chefId) return;
        fetchOrders();
    }, [chefId, queryKey, fetchOrders]);

    const checkForNewOrders = useCallback(async () => {
        const p = paramsRef.current;
        if (!p.chefId || baselineIdsRef.current == null) return;
        const query = buildQueryFrom(p);
        if (isUnboundedDeliveryQuery(query)) {
            return;
        }
        const seq = fetchSeqRef.current;
        try {
            const all = await getAllOrderData(p.chefId, query);
            const ids = (all?.orderDtoTreeSet || []).filter((o) => o?.id != null).map((o) => o.id);
            const baseline = baselineIdsRef.current;
            if (seq !== fetchSeqRef.current || baseline == null) return;
            setNewOrdersCount(ids.filter((id) => !baseline.has(id)).length);
        } catch (err) {
            console.error("Error checking for new orders:", err);
        }
    }, []);

    const refreshOrders = useCallback(async () => {
        setRefreshing(true);
        await fetchOrders({ force: true });
    }, [fetchOrders]);

    const updateStatus = useCallback(
        async (orderId, status, reason = "") => {
            try {
                await updateOrderStatus(orderId, status, reason);
                toastRef?.current?.addToast(
                    getTranslation("toast.statusUpdated", language) || "Order status successfully updated",
                    "success"
                );
                await fetchOrders({ force: true });
            } catch (err) {
                console.error("Failed to update order:", err);
                toastRef?.current?.addToast(
                    getTranslation("toast.updateFailed", language) || "Failed to update order status",
                    "error"
                );
                throw err;
            }
        },
        [fetchOrders, language, toastRef]
    );

    return {
        orderData,
        totalCount,
        loading,
        refreshing,
        error,
        newOrdersCount,
        fetchOrders,
        checkForNewOrders,
        refreshOrders,
        updateStatus
    };
};

/**
 * Polling for ongoing orders. Pass a quiet check (not a list reload) so staff don't lose their place.
 */
export const useOrderPolling = (checkFn, activeTab) => {
    useEffect(() => {
        if (activeTab !== "ongoing") return;

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkFn();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        const interval = setInterval(() => {
            if (!document.hidden) {
                checkFn();
            }
        }, 60000);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [checkFn, activeTab]);
};
